export function numberOrNull(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function textOrNull(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function isoOrNull(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(value) ||
      !/(Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null;
}

export function metricText(value, unit = '') {
  return value === null || value === undefined ? 'Not reported' :
    `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)}${unit ? ` ${unit}` : ''}`;
}

export function adaptTabletLog(record) {
  const payload = record.payload && typeof record.payload === 'object' ? record.payload : {};
  const tank = payload.tank || {};
  const sensor = payload.sensor || {};
  const rs485 = payload.rs485 || {};
  const vehicle = payload.vehicleDiagnostics || {};
  const issues = [];

  if (record.payload_valid !== true) issues.push('Unreadable telemetry');
  if (sensor.valid === false || sensor.crcValid === false || sensor.addressMatches === false ||
      sensor.functionMatches === false || rs485.crcValid === false) issues.push('Sensor reading invalid');
  if (rs485.timedOut === true) issues.push('Sensor timed out');
  if (rs485.connectionStatus === 'disconnected' || sensor.connectionStatus === 'disconnected') issues.push('Sensor disconnected');
  if (vehicle.connection?.connected === false) issues.push('Vehicle link disconnected');

  const faults = numberOrNull(vehicle.faults?.activeCount);
  if (faults !== null && faults > 0) issues.push(`${faults} active vehicle fault${faults === 1 ? '' : 's'}`);

  const readings = Object.entries(vehicle.snapshot?.readings || {}).map(([key, reading]) => {
    const display = numberOrNull(reading?.displayValue);
    const canonical = numberOrNull(reading?.canonicalValue);
    const useDisplay = display !== null && Boolean(textOrNull(reading?.displayUnit));
    return {
      key, label: textOrNull(reading?.label) || key,
      value: useDisplay ? display : canonical,
      unit: textOrNull(useDisplay ? reading?.displayUnit : reading?.canonicalUnit) || '',
      stale: reading?.stale === true,
      observedAt: isoOrNull(reading?.observedAt),
    };
  });

  if (readings.some((reading) => reading.stale)) issues.push('Vehicle readings stale');

  const knownHealth = sensor.valid === true && rs485.responded === true;
  const event = textOrNull(payload.event);
  const eventLabels = new Map([
    ['driver_dashboard_live_telemetry', 'Live telemetry'], ['truck_tank_live_telemetry', 'Live telemetry'],
    ['driver_dashboard_transfer_table', 'Transfer update'], ['driver_dashboard_job_log', 'Job update'],
    ['driver_dashboard_pto_control_action', 'PTO action'],
  ]);
  const type = eventLabels.get(event) || 'Other telemetry';

  const latitude = numberOrNull(payload.location?.latitude);
  const longitude = numberOrNull(payload.location?.longitude);
  const gpsValid = latitude !== null && longitude !== null && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;

  return {
    id: String(record.id), event, type,
    recordedAt: isoOrNull(payload.recordedAt) || isoOrNull(payload.postedAt),
    receivedAt: isoOrNull(record.received_at),
    level: numberOrNull(tank.volumePercent), volume: numberOrNull(tank.gallons),
    capacity: numberOrNull(tank.capacityGallons), remaining: numberOrNull(tank.gallonsRemaining),
    pressure: numberOrNull(sensor.pressurePsi), speed: numberOrNull(payload.location?.speedMph),
    motion: textOrNull(payload.audit?.motionState) || 'Not reported',
    gps: gpsValid ? `${latitude}, ${longitude}` : 'Not reported',
    faults, readings, issues,
    condition: issues.length ? 'Needs attention' : knownHealth ? 'Reported checks OK' : 'Health not reported',
    vehicleConnected: vehicle.connection?.connected === true ? 'Connected' :
      vehicle.connection?.connected === false ? 'Disconnected' : 'Not reported',
    sensorConnection: textOrNull(rs485.connectionStatus) || textOrNull(sensor.connectionStatus) || 'Not reported',
    eventId: textOrNull(payload.eventId), reason: textOrNull(payload.reason),
    appVersion: textOrNull(payload.app?.version),
    transfer: payload.transfer || null,
  };
}

export function adaptTabletLogsResponse(response) {
  if (response?.success !== true || response.schema_version !== 'tablet_telemetry_logs.v1' ||
      !Array.isArray(response.data) || !response.pagination ||
      typeof response.pagination.has_more !== 'boolean' || response.data.length > 100) {
    throw new Error('INVALID_TABLET_LOG_RESPONSE');
  }
  return { rows: response.data.map(adaptTabletLog), pagination: response.pagination };
}
